import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Minus, Plus, Trash2, ArrowRight } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

export default function Cart() {
  const { items, update, remove, subtotal, hydrated } = useCart();
  const navigate = useNavigate();
  const shipping = subtotal >= 499 || subtotal === 0 ? 0 : 49;

  if (!hydrated) {
    return <div className="min-h-[40vh] flex items-center justify-center text-[#666]">Loading your cart...</div>;
  }

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="cart-empty">
        <h1 className="font-serif text-5xl text-[#0A192F]">Your bag is empty</h1>
        <p className="mt-4 text-[#666]">Start your Ayurvedic ritual with our bestsellers.</p>
        <Link to="/shop" className="mt-8 inline-flex items-center gap-2 bg-[#E65A28] text-white px-8 py-4 rounded-full font-semibold hover:-translate-y-0.5 transition-transform">
          Shop now <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16" data-testid="cart-page">
      <h1 className="font-serif text-5xl text-[#0A192F] mb-10">Your Cart</h1>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-4">
          {items.map((it) => (
            <div key={it.product_id} className="flex gap-4 bg-white rounded-2xl p-4" data-testid={`cart-item-${it.product_id}`}>
              <img src={abs(it.image_url)} alt={it.name} className="h-28 w-28 object-contain rounded-xl bg-[#F4EBE1] p-2" />
              <div className="flex-1">
                <Link to={`/product/${it.slug}`} className="font-serif text-lg text-[#0A192F] hover:text-[#E65A28]">{it.name}</Link>
                <p className="text-[#666] text-sm mt-1">₹{it.price}</p>
                <div className="flex items-center gap-3 mt-3">
                  <div className="flex items-center border border-[#0A192F]/20 rounded-full">
                    <button onClick={() => update(it.product_id, it.quantity - 1)} className="p-2" data-testid={`decrease-${it.product_id}`}><Minus className="h-3 w-3" /></button>
                    <span className="px-3 text-sm font-medium">{it.quantity}</span>
                    <button onClick={() => update(it.product_id, it.quantity + 1)} className="p-2" data-testid={`increase-${it.product_id}`}><Plus className="h-3 w-3" /></button>
                  </div>
                  <button onClick={() => remove(it.product_id)} className="text-[#666] hover:text-[#E65A28]" data-testid={`remove-${it.product_id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-[#0A192F]">₹{(it.price * it.quantity).toFixed(0)}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-4">
          <div className="bg-white rounded-2xl p-6 sticky top-32" data-testid="cart-summary">
            <h3 className="font-serif text-2xl text-[#0A192F] mb-4">Order Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#666]">Subtotal</span><span className="font-medium">₹{subtotal.toFixed(0)}</span></div>
              <div className="flex justify-between"><span className="text-[#666]">Shipping</span><span className="font-medium">{shipping === 0 ? "Free" : `₹${shipping}`}</span></div>
              <div className="border-t border-[#0A192F]/10 pt-3 mt-3 flex justify-between text-lg font-semibold">
                <span>Total</span><span>₹{(subtotal + shipping).toFixed(0)}</span>
              </div>
            </div>
            <button
              onClick={() => navigate("/checkout")}
              className="mt-6 w-full bg-[#E65A28] text-white py-4 rounded-full font-semibold hover:-translate-y-0.5 transition-transform"
              data-testid="checkout-btn"
            >
              Proceed to checkout
            </button>
            <Link to="/shop" className="mt-3 block text-center text-sm text-[#0A192F] hover:text-[#E65A28]">Continue shopping</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
